import React, { Fragment, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import { useAuth } from '../../Context/Context';

import SideNav from '../../Components/SideNav';
import PanelEdicion from '../../Components/PanelEdicion';
import PreviewSitio from '../../Components/PreviewSitio';
import SeccionPanel from '../../Components/SeccionPanel';

import usePanelEdicion, { confirmarDescartar } from '../../Hooks/usePanelEdicion';
import usePreviewSitio from '../../Hooks/usePreviewSitio';

import licenciaturasHelper from '../../Helpers/Licenciaturas';
import { obtenerEmbedYoutube } from '../../Helpers/youtube';

const licenciaturas = {
    cine: { nombre: 'Licenciatura en Cine Digital', corto: 'Cine', carrera: 'de Cine Digital.', ruta: '/licenciatura-en-cine', video: true, ingresoAnual: false },
    animacion: { nombre: 'Licenciatura en Animación', corto: 'Animación', carrera: 'de Animación.', ruta: '/licenciatura-en-animacion', video: true, ingresoAnual: false },
    multimedia: { nombre: 'Licenciatura en Multimedia y Medios Interactivos', corto: 'Multimedia', carrera: 'de Multimedia.', ruta: '/multimedia', video: true, ingresoAnual: true },
    marketing: { nombre: 'Licenciatura en Marketing Digital Creativo', corto: 'Marketing', carrera: 'de Marketing.', ruta: '/licenciatura-marketing-digital', video: false, ingresoAnual: true }
};

const estiloAyuda = { color: "#757575", fontSize: "13px", margin: "-10px 0 10px" };

const seccionDeCampo = campo => {
    if (campo.startsWith('charla_')) return 'charla';
    if (campo.startsWith('semestre_')) return 'plan';
    if (campo === 'horario' || campo === 'horario_nota') return 'horario';
    if (campo === 'video') return 'video';
    return 'inicio';
};

const Switch = ({ id, etiqueta, checked, onChange }) => (
    <div className="switch" style={{margin:"10px 0 20px"}}>
        <p style={{margin:"0 0 5px"}}>{etiqueta}</p>
        <label>
            No
            <input id={id} type="checkbox" checked={Boolean(checked)} onChange={e => onChange(e.target.checked)} />
            <span className="lever"></span>
            Sí
        </label>
    </div>
);

const Licenciatura = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const config = licenciaturas[slug];
    const { editar, setEditar } = useAuth();

    const [guardado, setGuardado] = useState(null);
    const [seccionActiva, setSeccionActiva] = useState(null);
    const [planAbierto, setPlanAbierto] = useState(false);

    useEffect(() => {
        if (!licenciaturas[slug]) return;
        setGuardado(null);
        setSeccionActiva(null);
        setPlanAbierto(false);
        setEditar({});
        licenciaturasHelper.obtenerLicenciatura(slug).then(res => {
            if (res.success) setGuardado(res.data);
            else Swal.fire('Error!', 'No se pudo obtener la licenciatura, codigo: ' + res.code, 'error');
        });
    }, [slug, setEditar]);

    // Al cerrar el panel se limpia `editar`; entonces el iframe vuelve a mostrar lo guardado
    const borrador = editar._id ? editar : guardado;
    const hayCambios = Boolean(editar._id) && JSON.stringify(editar) !== JSON.stringify(guardado);

    const panel = usePanelEdicion({ hayCambios, onCerrar: () => setEditar({}) });

    const abrirPanel = () => {
        if (!editar._id && guardado) setEditar(guardado);
        panel.abrir('editar');
    }

    const seleccionarCampo = ({ id }) => {
        abrirPanel();
        setSeccionActiva(seccionDeCampo(id));
        if (id.startsWith('semestre_')) setPlanAbierto(true);
        // Los campos se montan hasta que `editar` tiene el documento
        setTimeout(() => {
            if (id.startsWith('semestre_')) return document.getElementById(id)?.scrollIntoView({ block: 'center' });
            document.getElementById(id === 'proximo_inicio' ? 'proximo_inicio_0' : id)?.focus();
        }, 100);
    }

    const { iframeRef, src } = usePreviewSitio({
        page: 'licenciatura-' + slug,
        ruta: config?.ruta ?? '',
        data: borrador,
        onSelect: seleccionarCampo
    });

    const cambiarLicenciatura = async nuevo => {
        if (nuevo === slug) return;
        if (hayCambios && !(await confirmarDescartar())) return;
        panel.cerrar({ forzar: true });
        navigate('/licenciaturas/' + nuevo);
    }

    const descartar = async () => {
        if (await confirmarDescartar()) setEditar(guardado);
    }

    const irASeccion = clave => {
        setSeccionActiva(clave);
        if (clave === 'plan') setPlanAbierto(true);
    }

    const cambiar = (campo, valor) => setEditar(editar => ({ ...editar, [campo]: valor }));

    const leerDato = e => cambiar(e.target.name, e.target.value);

    const leerProximoInicio = (i, valor) => setEditar(editar => {
        const proximo_inicio = [...(editar.proximo_inicio ?? ['', ''])];
        proximo_inicio[i] = valor;
        return { ...editar, proximo_inicio };
    });

    const leerVideo = e => cambiar('video', obtenerEmbedYoutube(e.target.value) ?? e.target.value);

    const leerSemestre = (i, valor) => setEditar(editar => ({
        ...editar,
        semestre: editar.semestre.map((semestre, j) => (j === i ? { ...semestre, data: valor } : semestre))
    }));

    const videoInvalido = Boolean(config?.video && editar.video && !obtenerEmbedYoutube(editar.video));

    const actualizar = async e => {
        e.preventDefault();
        const faltantes = [];
        if (!editar.inicio?.trim()) faltantes.push('Inicio de clases');
        if (editar.mostrar_proximo_inicio && !editar.proximo_inicio?.[0]?.trim()) faltantes.push('Próximo inicio');
        if (config.ingresoAnual && !editar.mostrar_proximo_inicio && !editar.mes_ingreso_anual?.trim()) faltantes.push('Mes de ingreso anual');
        if (config.video && !editar.video?.trim()) faltantes.push('Video');
        if (!editar.horario?.trim()) faltantes.push('Horario');
        if (editar.charla_activa) {
            if (!editar.charla_titulo?.trim()) faltantes.push('Título del aviso');
            if (!editar.charla_mensaje?.trim()) faltantes.push('Mensaje del aviso');
            if (!editar.charla_boton?.trim()) faltantes.push('Texto del botón');
        }
        editar.semestre.forEach(({ data }, i) => {
            if (!data?.trim()) faltantes.push('Cuatrimestre ' + (i + 1));
        });
        if (faltantes.length) return Swal.fire('Error!', 'Faltan datos por llenar: ' + faltantes.join(', '), 'error');
        if (videoInvalido) return Swal.fire('Error!', 'No se reconoce el enlace de YouTube', 'error');

        const res = await licenciaturasHelper.editarLicenciatura(editar, editar._id);
        if (res?.success) {
            setGuardado(res.data);
            panel.cerrar({ forzar: true });
        }
    }

    if (!config) return <Navigate to="/licenciaturas/cine" replace />;

    const seccion = clave => ({ id: clave, activa: seccionActiva === clave, onFocus: () => setSeccionActiva(clave) });
    let numero = 0;

    const secciones = [
        { id: 'charla', titulo: 'Aviso' },
        ...(config.video ? [{ id: 'video', titulo: 'Video' }] : []),
        { id: 'inicio', titulo: 'Inicio de clases' },
        { id: 'horario', titulo: 'Horario' },
        { id: 'plan', titulo: 'Plan de estudios' }
    ];

    const input = (campo, etiqueta, ayuda) => (
        <Fragment>
            <div className="input-field">
                <input onChange={leerDato} value={editar[campo] ?? ''} id={campo} name={campo} type="text" className="validate" />
                <label className='active' htmlFor={campo}>{etiqueta}</label>
            </div>
            {ayuda && <p style={estiloAyuda}>{ayuda}</p>}
        </Fragment>
    );

    return (
        <Fragment>
            <SideNav />
            <div className='row'>
                <PanelEdicion
                    abierto={panel.abierto === 'editar'}
                    titulo={'Editar ' + config.corto}
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    secciones={secciones}
                    seccionActiva={seccionActiva}
                    onSeccion={irASeccion}
                    hayCambios={hayCambios}
                    pie={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_secundario' onClick={descartar} disabled={!hayCambios}>
                                Descartar
                            </button>
                            <button type='submit' form='form_licenciatura' className='boton_admin' disabled={!hayCambios}>
                                Guardar cambios
                            </button>
                        </Fragment>
                    }
                >
                    {editar._id && (
                        <form id='form_licenciatura' onSubmit={actualizar}>
                            <SeccionPanel numero={++numero} titulo='Aviso de charlas informativas' ubicacion='Ventana emergente que aparece al entrar a la página. En la vista previa se muestra como una franja arriba.' {...seccion('charla')}>
                                <Switch
                                    id='charla_activa'
                                    etiqueta='Mostrar aviso al entrar a la página'
                                    checked={editar.charla_activa}
                                    onChange={valor => cambiar('charla_activa', valor)}
                                />
                                {input('charla_titulo', 'Título', 'Texto en negritas, arriba del mensaje')}
                                {input('charla_mensaje', 'Mensaje', `Ejemplo: ${config.carrera} Lunes 1 de junio, 5:00 pm.`)}
                                {input('charla_boton', 'Texto del botón', 'El botón abre WhatsApp')}
                            </SeccionPanel>

                            {config.video && (
                                <SeccionPanel numero={++numero} titulo='Video' ubicacion='Video de YouTube en la parte superior de la página.' {...seccion('video')}>
                                    <div className="input-field">
                                        <input onChange={leerVideo} value={editar.video ?? ''} id='video' name='video' type="text" className="validate" />
                                        <label className='active' htmlFor='video'>Link de YouTube</label>
                                    </div>
                                    <p style={videoInvalido ? { ...estiloAyuda, color: "#c62828" } : estiloAyuda}>
                                        {videoInvalido ? 'No se reconoce el enlace de YouTube' : 'Pega el link de YouTube (de la barra del navegador, de Compartir o de youtu.be)'}
                                    </p>
                                </SeccionPanel>
                            )}

                            <SeccionPanel numero={++numero} titulo='Inicio de clases' ubicacion='Fechas que aparecen en el bloque "Inicio de clases".' {...seccion('inicio')}>
                                {input('inicio', 'Inicio de clases')}
                                <Switch
                                    id='mostrar_proximo_inicio'
                                    etiqueta='Mostrar próximo inicio'
                                    checked={editar.mostrar_proximo_inicio}
                                    onChange={valor => cambiar('mostrar_proximo_inicio', valor)}
                                />
                                {[0, 1].map(i => (
                                    <div className="input-field" key={i}>
                                        <input onChange={e => leerProximoInicio(i, e.target.value)} value={editar.proximo_inicio?.[i] ?? ''} id={'proximo_inicio_' + i} type="text" className="validate" />
                                        <label className='active' htmlFor={'proximo_inicio_' + i}>Próximo inicio {i + 1}</label>
                                    </div>
                                ))}
                                {config.ingresoAnual && input('mes_ingreso_anual', 'Mes de ingreso anual', 'Se ve como: ingreso anual, en … Se oculta cuando el próximo inicio está activo.')}
                            </SeccionPanel>

                            <SeccionPanel numero={++numero} titulo='Horario' ubicacion='Texto debajo del título "Horario".' {...seccion('horario')}>
                                {input('horario', 'Horario')}
                                {input('horario_nota', 'Nota del horario', 'Segunda línea, debajo del horario. Déjalo vacío para ocultarlo')}
                            </SeccionPanel>

                            <SeccionPanel numero={++numero} titulo='Plan de estudios' ubicacion='Los 9 cuatrimestres de "Plan de estudios", al final de la página.' {...seccion('plan')}>
                                <button type='button' className='boton_admin boton_admin_secundario' style={{marginBottom: planAbierto ? "15px" : 0}} onClick={() => setPlanAbierto(!planAbierto)}>
                                    {planAbierto ? 'Ocultar cuatrimestres' : 'Mostrar los 9 cuatrimestres'}
                                </button>
                                {planAbierto && editar.semestre?.map((semestre, i) => (
                                    <div id={'semestre_' + i} key={slug + '_' + i} style={{marginBottom:"20px"}}>
                                        <p style={{margin:"0 0 5px"}}>Cuatrimestre {i + 1}</p>
                                        <CKEditor
                                            editor={ClassicEditor}
                                            data={semestre.data}
                                            onChange={(event, editor) => leerSemestre(i, editor.getData())}
                                        />
                                    </div>
                                ))}
                            </SeccionPanel>
                        </form>
                    )}
                </PanelEdicion>
                <PreviewSitio
                    iframeRef={iframeRef}
                    src={src}
                    titulo={'Vista previa de ' + config.nombre}
                    margen={panel.abierto ? panel.ancho : 0}
                    encabezado={config.nombre}
                    ayuda='Haz clic en el aviso, una fecha, el horario, el video o un cuatrimestre para editarlo'
                    acciones={
                        <Fragment>
                            <select
                                className='browser-default boton_admin boton_admin_secundario'
                                style={{width:"auto"}}
                                value={slug}
                                onChange={e => cambiarLicenciatura(e.target.value)}
                            >
                                {Object.entries(licenciaturas).map(([valor, { corto }]) => (
                                    <option key={valor} value={valor}>{corto}</option>
                                ))}
                            </select>
                            <button type='button' className='boton_admin' onClick={abrirPanel} disabled={!guardado}>
                                Editar información
                            </button>
                        </Fragment>
                    }
                />
            </div>
        </Fragment>
    );
}

export default Licenciatura;
